import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  Plane, Clock, ShieldCheck, MapPin, Phone, MessageCircle,
  Languages, CheckCircle2, ArrowRight, Star, Car,
} from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { FloatingCTAs } from './FloatingCTAs';
import { BottomCTA } from './BottomCTA';
import { CookieConsent } from './CookieConsent';
import { getAirportTransferContent, AIRPORT_TRANSFER_CONTENT } from '../data/pages/porto-airport-transfer';
import { SUPPORTED_LANGUAGES } from '../i18n';

const SITE_URL = 'https://govipportugal.com';
const SLUG = 'porto-airport-private-transfer';

/**
 * Custom SEO manager for this specific landing page.
 * Handles: <title>, meta description, canonical, hreflang (all 4 langs),
 * OG/Twitter tags, TaxiService + FAQPage + BreadcrumbList schema.
 */
const useAirportTransferSEO = (lang, content) => {
  useEffect(() => {
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tagName || 'meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'tagName') return;
        el.setAttribute(k, v);
      });
    };

    const seo = content.seo;
    const canonicalHref = `${SITE_URL}/${lang}/${SLUG}`;

    document.title = seo.title;
    upsert('meta[name="description"]', { name: 'description', content: seo.description });
    upsert('meta[name="keywords"]', { name: 'keywords', content: seo.keywords });
    upsert('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsert('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsert('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsert('meta[property="og:url"]', { property: 'og:url', content: canonicalHref });
    upsert('meta[property="og:image"]', { property: 'og:image', content: AIRPORT_TRANSFER_CONTENT.meta.ogImage });
    upsert('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsert('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    upsert('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
    upsert('link[rel="canonical"]', { tagName: 'link', rel: 'canonical', href: canonicalHref });

    // hreflang alternates — clean first, then add
    document.head.querySelectorAll('link[rel="alternate"][data-hreflang]').forEach((el) => el.remove());
    SUPPORTED_LANGUAGES.forEach((code) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', `${SITE_URL}/${code}/${SLUG}`);
      link.setAttribute('data-hreflang', '1');
      document.head.appendChild(link);
    });
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${SITE_URL}/en/${SLUG}`);
    xDefault.setAttribute('data-hreflang', '1');
    document.head.appendChild(xDefault);

    // Schema.org — TaxiService + FAQ + Breadcrumbs
    const schemaId = 'airport-transfer-schema';
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement('script');
      schemaTag.id = schemaId;
      schemaTag.type = 'application/ld+json';
      document.head.appendChild(schemaTag);
    }

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'TaxiService',
          '@id': `${canonicalHref}#service`,
          name: seo.title,
          description: seo.description,
          serviceType: 'Airport Transfer',
          provider: { '@type': 'LocalBusiness', name: 'GO VIP Portugal', url: `${SITE_URL}/${lang}`, telephone: '+351932651940' },
          areaServed: { '@type': 'City', name: 'Porto' },
          offers: content.destinations.items.map((d) => ({
            '@type': 'Offer',
            name: d.name,
            price: d.price.replace('€', ''),
            priceCurrency: 'EUR',
          })),
        },
        {
          '@type': 'FAQPage',
          mainEntity: content.faq.items.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/${lang}` },
            { '@type': 'ListItem', position: 2, name: content.hero.title, item: canonicalHref },
          ],
        },
      ],
    };
    schemaTag.textContent = JSON.stringify(schema);
  }, [lang, content]);
};

// ---- SUB-SECTIONS ----

const HeroSection = ({ content, lang, whatsappUrl }) => (
  <section className="relative min-h-[92vh] overflow-hidden bg-[#0a0a0a] pt-24 pb-16 md:pt-32">
    {/* Backdrop image */}
    <div className="absolute inset-0">
      <img
        src={AIRPORT_TRANSFER_CONTENT.meta.heroImage}
        alt={content.hero.title}
        className="h-full w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
    </div>

    <div className="relative container mx-auto max-w-6xl px-4">
      <div className="max-w-2xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-300 ring-1 ring-amber-400/30">
          <Plane className="h-3.5 w-3.5" />
          {content.hero.kicker}
        </div>

        <h1 className="mb-5 font-serif text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl" data-testid="page-title">
          {content.hero.title}
        </h1>
        <p className="mb-8 text-lg leading-relaxed text-zinc-300 md:text-xl max-w-xl">
          {content.hero.subtitle}
        </p>

        {/* Badges */}
        <div className="mb-8 flex flex-wrap gap-2">
          {content.hero.badges.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm ring-1 ring-white/15">
              <CheckCircle2 className="h-3.5 w-3.5 text-amber-400" />
              {b}
            </span>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="flex flex-wrap gap-3">
          <Link
            to={`/${lang}#booking`}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-black shadow-xl hover:from-amber-400 hover:to-amber-500 transition-all"
            data-testid="hero-book-cta"
          >
            {content.hero.cta_primary}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm hover:bg-white/10 transition-all"
            data-testid="hero-whatsapp-cta"
          >
            <MessageCircle className="h-4 w-4" />
            {content.hero.cta_secondary}
          </a>
        </div>

        {/* Trust indicators */}
        <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-zinc-400">
          <div className="flex items-center gap-1.5">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="ml-1 font-medium text-white">5.0</span>
          </div>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> 24/7</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Licensed & insured</span>
        </div>
      </div>
    </div>
  </section>
);

const BenefitsSection = ({ content }) => (
  <section className="bg-white py-20 md:py-28">
    <div className="container mx-auto max-w-6xl px-4">
      <h2 className="mb-14 font-serif text-3xl font-bold leading-tight text-gray-900 md:text-4xl lg:text-5xl max-w-2xl">
        {content.benefits.title}
      </h2>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {content.benefits.items.map((item, i) => (
          <div
            key={i}
            className="group relative rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:border-amber-300 hover:shadow-xl"
          >
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100 transition-all group-hover:bg-amber-500 group-hover:text-white">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h3 className="mb-2 font-serif text-xl font-bold text-gray-900">{item.title}</h3>
            <p className="text-sm leading-relaxed text-gray-600">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorksSection = ({ content }) => (
  <section className="relative overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-[#111] py-20 md:py-28">
    <div
      aria-hidden
      className="pointer-events-none absolute -top-40 right-1/2 h-[500px] w-[500px] translate-x-1/2 rounded-full opacity-25 blur-3xl"
      style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.5), transparent 70%)' }}
    />
    <div className="container relative mx-auto max-w-6xl px-4">
      <h2 className="mb-14 text-center font-serif text-3xl font-bold text-white md:text-5xl">
        {content.howItWorks.title}
      </h2>
      <div className="grid gap-8 md:grid-cols-4">
        {content.howItWorks.steps.map((step, i) => (
          <div key={i} className="relative text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-2xl font-bold text-black shadow-lg">
              {step.n}
            </div>
            <h3 className="mb-2 font-serif text-lg font-bold text-white">{step.title}</h3>
            <p className="text-sm text-zinc-400">{step.text}</p>
            {i < content.howItWorks.steps.length - 1 && (
              <div className="pointer-events-none absolute top-8 left-full hidden h-px w-full -translate-x-1/2 bg-gradient-to-r from-amber-500/40 to-transparent md:block" />
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DestinationsSection = ({ content, lang }) => (
  <section className="bg-gradient-to-b from-white to-amber-50/40 py-20 md:py-28">
    <div className="container mx-auto max-w-6xl px-4">
      <div className="mb-10 max-w-3xl">
        <h2 className="mb-4 font-serif text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
          {content.destinations.title}
        </h2>
        <p className="text-base leading-relaxed text-gray-600 md:text-lg">
          {content.destinations.subtitle}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {content.destinations.items.map((d, i) => (
          <Link
            key={i}
            to={`/${lang}#booking`}
            className="group flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-amber-400 hover:shadow-lg"
            data-testid={`destination-${i}`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-gray-900">{d.name}</h3>
                <p className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="h-3 w-3" /> {d.time}
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <span className="font-serif text-xl font-bold text-amber-600">{d.price}</span>
              <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-amber-600" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const FAQSection = ({ content }) => (
  <section className="bg-white py-20 md:py-28">
    <div className="container mx-auto max-w-3xl px-4">
      <h2 className="mb-10 font-serif text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
        {content.faq.title}
      </h2>
      <div className="space-y-3">
        {content.faq.items.map((item, i) => (
          <details
            key={i}
            className="group rounded-xl border border-gray-200 bg-white p-5 open:border-amber-300 open:bg-amber-50/40 transition-all"
            data-testid={`faq-item-${i}`}
          >
            <summary className="flex cursor-pointer items-start justify-between gap-4 font-semibold text-gray-900">
              <span className="pr-4">{item.q}</span>
              <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 transition-transform group-open:rotate-45">
                <span className="text-lg leading-none">+</span>
              </span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = ({ content, lang, whatsappUrl }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 py-20 text-center">
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-20"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.2) 0%, transparent 40%)',
      }}
    />
    <div className="container relative mx-auto max-w-3xl px-4">
      <h2 className="mb-4 font-serif text-3xl font-bold text-black md:text-5xl">
        {content.cta.title}
      </h2>
      <p className="mb-8 text-base text-black/80 md:text-lg">
        {content.cta.subtitle}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link
          to={`/${lang}#booking`}
          className="group inline-flex items-center gap-2 rounded-full bg-black px-8 py-4 text-base font-semibold text-white shadow-2xl hover:bg-gray-900 transition-all"
          data-testid="cta-book"
        >
          {content.cta.button}
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </Link>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border-2 border-black bg-white/10 px-8 py-4 text-base font-semibold text-black backdrop-blur-sm hover:bg-white/20 transition-all"
          data-testid="cta-whatsapp"
        >
          <MessageCircle className="h-5 w-5" />
          {content.cta.whatsapp}
        </a>
      </div>
    </div>
  </section>
);

// ---- MAIN PAGE ----

export const PortoAirportTransferPage = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || 'en').split('-')[0];
  const content = getAirportTransferContent(lang);
  const whatsappUrl = `https://wa.me/351932651940?text=${encodeURIComponent(
    `Olá! Gostaria de reservar um transfer do Aeroporto do Porto.`
  )}`;

  useAirportTransferSEO(lang, content);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="bg-white">
      <Header />
      <main data-testid="porto-airport-transfer-page">
        <HeroSection content={content} lang={lang} whatsappUrl={whatsappUrl} />
        <BenefitsSection content={content} />
        <HowItWorksSection content={content} />
        <DestinationsSection content={content} lang={lang} />
        <FAQSection content={content} />
        <CTASection content={content} lang={lang} whatsappUrl={whatsappUrl} />
      </main>
      <BottomCTA />
      <Footer />
      <FloatingCTAs />
      <CookieConsent />
    </div>
  );
};

export default PortoAirportTransferPage;
